import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Sparkles, FileDown, Presentation, FileText, Printer, Upload, Wand2,
  Check, X, RefreshCw, Video, Bot, Library, ScrollText, Network, Brain, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { generateStudioLesson, regenerateStudioSection } from "@/lib/curriculum-studio.functions";
import { STUDIO_SECTIONS, type StudioLesson, type StudioSource } from "@/lib/curriculum-studio-types";
import { extractLessonSource } from "@/lib/lesson-source-extract";
import {
  downloadStudioPDF, downloadStudioPPTX, downloadStudioDOCX,
  exportStudioToGoogleDocs, printStudioWorksheet,
} from "@/lib/studio-exports";

export const Route = createFileRoute("/teacher/studio")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({
    meta: [
      { title: "AI Curriculum Studio · MAKAI Teachers" },
      { name: "description", content: "Type one line and MAKAI builds a complete NCDC-aligned lesson: plan, activities, quiz, worksheet, marking guide and exports." },
      { property: "og:title", content: "AI Curriculum Studio · MAKAI" },
      { property: "og:description", content: "The fastest lesson preparation studio for Ugandan teachers." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const DURATIONS = [30, 40, 45, 60, 80, 90, 120];

const SOURCES: { value: StudioSource; label: string; hint: string }[] = [
  { value: "topic", label: "Topic", hint: "Create a Primary 6 Mathematics lesson on Fractions aligned with the Ugandan curriculum." },
  { value: "objective", label: "Curriculum objective", hint: "Learners should be able to describe the process of respiration in humans." },
  { value: "chapter", label: "Textbook chapter", hint: "MK Biology Book 2, Chapter 7: Respiration." },
  { value: "document", label: "Uploaded document", hint: "Build a lesson from the uploaded syllabus, notes, PDF or PowerPoint." },
];

const QUICK_PROMPTS = [
  "Create a Primary 6 Mathematics lesson on Fractions aligned with the Ugandan curriculum.",
  "Create a Senior 2 Biology lesson on Respiration.",
  "Create a Primary 4 English lesson on Nouns with group work.",
  "Create a Senior 3 Physics lesson on Reflection of light with practical activities.",
];

function Page() {
  const ctx = useTeacherContext();
  const generate = useServerFn(generateStudioLesson);
  const fileRef = useRef<HTMLInputElement>(null);

  const [source, setSource] = useState<StudioSource>("topic");
  const [request, setRequest] = useState("");
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [duration, setDuration] = useState(40);
  const [sourceText, setSourceText] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [busy, setBusy] = useState(false);
  const [lesson, setLesson] = useState<StudioLesson | null>(null);

  const subjectOptions = useMemo(
    () => Array.from(new Set(ctx.subjects.map((s) => s.name))).filter(Boolean),
    [ctx.subjects],
  );
  const classOptions = useMemo(
    () => Array.from(new Set(ctx.streams.map((s) => `${s.class_name} ${s.name}`))),
    [ctx.streams],
  );

  async function onFile(file?: File | null) {
    if (!file) return;
    const t = toast.loading(`Reading ${file.name}…`);
    try {
      const text = await extractLessonSource(file);
      setSourceText(text);
      setSourceName(file.name);
      setSource("document");
      toast.success(`Loaded ${file.name} (${text.length.toLocaleString()} characters)`, { id: t });
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    }
  }

  async function run() {
    const prompt = request.trim() || (sourceText ? `Build a lesson from the uploaded material: ${sourceName}` : "");
    if (!prompt) {
      toast.error("Describe the lesson you want, or upload source material.");
      return;
    }
    setBusy(true);
    setLesson(null);
    const t = toast.loading("MAKAI is preparing your lesson…");
    try {
      const out = await generate({
        data: {
          request: prompt,
          source,
          subject: subject || undefined,
          className: className || undefined,
          durationMins: duration,
          sourceText: sourceText || undefined,
        },
      });
      setLesson(out);
      toast.success("Lesson ready — every section is editable", { id: t });
    } catch (e) {
      toast.error((e as Error).message ?? "Generation failed", { id: t });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell role="teacher" title="AI Curriculum Studio" subtitle="Type one line. MAKAI builds the whole lesson.">
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card className="h-fit lg:sticky lg:top-24">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wand2 className="h-4 w-4 text-primary" /> Lesson brief
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Start from</Label>
              <Select value={source} onValueChange={(v) => setSource(v as StudioSource)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>What should MAKAI prepare?</Label>
              <Textarea
                rows={3}
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder={SOURCES.find((s) => s.value === source)?.hint}
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => { setRequest(q); setSource("topic"); }}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                  >
                    {q.split(" lesson")[0].replace("Create a ", "")}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Source material (optional)</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.pptx,.txt,.md,.csv"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
              <Button type="button" variant="outline" className="w-full gap-2" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> {sourceName || "Upload PDF, DOCX, PPTX or notes"}
              </Button>
              {sourceName && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline"
                  onClick={() => { setSourceText(""); setSourceName(""); }}
                >
                  Remove {sourceName}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5 min-w-0">
                <Label>Subject</Label>
                {subjectOptions.length ? (
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Auto" />
                )}
              </div>
              <div className="space-y-1.5 min-w-0">
                <Label>Class</Label>
                {classOptions.length ? (
                  <Select value={className} onValueChange={setClassName}>
                    <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
                    <SelectContent>
                      {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Auto" />
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={run} disabled={busy} className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? "Preparing…" : "Prepare lesson"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Aligned to the Uganda NCDC competency-based curriculum and UNEB assessment style.
            </p>
          </CardContent>
        </Card>

        <div className="min-w-0">
          {!lesson && !busy && <EmptyState />}
          {busy && (
            <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm">Building objectives, activities, quiz, worksheet and marking guide…</p>
            </CardContent></Card>
          )}
          {lesson && <StudioResult lesson={lesson} onChange={setLesson} />}
        </div>
      </div>
    </AppShell>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <Sparkles className="h-10 w-10 text-primary/60" />
        <p className="max-w-lg text-sm text-muted-foreground">
          Type something like <em>"Create a Primary 6 Mathematics lesson on Fractions aligned with the Ugandan
          curriculum"</em> — MAKAI returns objectives, outcomes, curriculum references, a timed plan, activities,
          worked examples, quiz, structured questions, marking guide, revision notes, flashcards and parent
          follow-up. Edit anything, then export.
        </p>
        <div className="flex flex-wrap justify-center gap-1.5 text-[11px] text-muted-foreground">
          {STUDIO_SECTIONS.map((s) => (
            <span key={String(s.key)} className="rounded-full border px-2 py-0.5">{s.label}</span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------ Result view ------------------------------ */

function StudioResult({ lesson, onChange }: { lesson: StudioLesson; onChange: (l: StudioLesson) => void }) {
  const [exporting, setExporting] = useState<string | null>(null);

  async function withBusy(name: string, fn: () => void | Promise<void>) {
    setExporting(name);
    try { await fn(); } catch (e) { toast.error((e as Error).message ?? "Export failed"); }
    finally { setExporting(null); }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-3">
          <div className="min-w-0 space-y-1.5">
            <CardTitle className="text-xl">{lesson.meta?.topic}</CardTitle>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary">{lesson.meta?.subject}</Badge>
              <Badge variant="secondary">{lesson.meta?.class}</Badge>
              <Badge variant="secondary">{lesson.meta?.durationMins} min</Badge>
              <Badge>{lesson.meta?.curriculum ?? "Uganda NCDC"}</Badge>
              {lesson.meta?.strand && <Badge variant="outline">{lesson.meta.strand}</Badge>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{lesson.overview}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("pdf", () => downloadStudioPDF(lesson))}><FileDown className="h-4 w-4" /> PDF</Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("docx", () => downloadStudioDOCX(lesson))}><FileText className="h-4 w-4" /> DOCX</Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("pptx", () => downloadStudioPPTX(lesson))}><Presentation className="h-4 w-4" /> PowerPoint</Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("gdocs", () => exportStudioToGoogleDocs(lesson))}><BookOpen className="h-4 w-4" /> Google Docs</Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("ws", () => printStudioWorksheet(lesson))}><Printer className="h-4 w-4" /> Worksheet</Button>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!!exporting} onClick={() => withBusy("wsa", () => printStudioWorksheet(lesson, { answers: true }))}><Printer className="h-4 w-4" /> Worksheet + answers</Button>
          </div>
          <IntegrationBar lesson={lesson} />
        </CardContent>
      </Card>

      {STUDIO_SECTIONS.map((s) => (
        <SectionCard
          key={String(s.key)}
          sectionKey={String(s.key)}
          label={s.label}
          value={lesson[s.key]}
          context={`${lesson.meta?.subject} · ${lesson.meta?.class} · ${lesson.meta?.topic} · ${lesson.meta?.durationMins} min`}
          onSave={(v) => onChange({ ...lesson, [s.key]: v } as StudioLesson)}
        />
      ))}
    </div>
  );
}

function IntegrationBar({ lesson }: { lesson: StudioLesson }) {
  function stash(target: string) {
    try {
      sessionStorage.setItem("makai:studio:lesson", JSON.stringify(lesson));
    } catch { /* storage may be unavailable */ }
    toast.success(`Lesson sent to ${target}`);
  }
  const items = [
    { to: "/teacher/live", label: "Makai Live", icon: Video },
    { to: "/teacher/tutor", label: "AI Tutor", icon: Bot },
    { to: "/teacher/questions", label: "Question Generator", icon: Library },
    { to: "/teacher/exams", label: "Exam Builder", icon: ScrollText },
    { to: "/teacher/intelligence", label: "Learner Intelligence", icon: Brain },
    { to: "/learner/tree", label: "Learning Tree", icon: Network },
  ] as const;
  return (
    <div className="flex flex-wrap items-center gap-2 border-t pt-3">
      <span className="text-xs font-medium text-muted-foreground">Send to:</span>
      {items.map((it) => (
        <Button key={it.to} asChild size="sm" variant="ghost" className="h-7 gap-1.5 px-2 text-xs">
          <Link to={it.to} onClick={() => stash(it.label)}>
            <it.icon className="h-3.5 w-3.5" /> {it.label}
          </Link>
        </Button>
      ))}
    </div>
  );
}

/* ------------------------- Editable section card ------------------------- */

type Primitive = string | string[];

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function SectionCard({
  sectionKey, label, value, context, onSave,
}: {
  sectionKey: string;
  label: string;
  value: unknown;
  context: string;
  onSave: (v: unknown) => void;
}) {
  const regenerate = useServerFn(regenerateStudioSection);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const kind: "text" | "list" | "json" =
    typeof value === "string" ? "text" : isStringArray(value) ? "list" : "json";

  function startEdit() {
    setDraft(kind === "text" ? String(value ?? "") : kind === "list" ? (value as string[]).join("\n") : JSON.stringify(value ?? null, null, 2));
    setEditing(true);
  }

  function save() {
    try {
      if (kind === "text") onSave(draft);
      else if (kind === "list") onSave(draft.split("\n").map((l) => l.trim()).filter(Boolean));
      else onSave(JSON.parse(draft));
      setEditing(false);
      toast.success(`${label} updated`);
    } catch {
      toast.error("That is not valid JSON — check the structure.");
    }
  }

  async function askAi() {
    setBusy(true);
    try {
      const res = await regenerate({
        data: {
          section: sectionKey,
          sectionLabel: label,
          currentJson: JSON.stringify(value ?? null).slice(0, 20000),
          context,
          instruction: instruction || undefined,
        },
      });
      const next = JSON.parse(res.json);
      if (next === null || next === undefined) throw new Error("Empty response");
      onSave(next);
      setInstruction("");
      setEditing(false);
      toast.success(`${label} regenerated`);
    } catch (e) {
      toast.error((e as Error).message ?? "Could not regenerate");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{label}</CardTitle>
        <div className="flex shrink-0 gap-1">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={save}><Check className="h-3.5 w-3.5" /> Save</Button>
              <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /> Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={startEdit}>Edit</Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" disabled={busy} onClick={askAi}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Redo
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {editing ? (
          <>
            <Textarea rows={kind === "json" ? 12 : 6} value={draft} onChange={(e) => setDraft(e.target.value)} className="font-mono text-xs" />
            <Input
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Tell MAKAI how to redo this section (optional)…"
              className="text-xs"
            />
          </>
        ) : (
          <SectionView value={value} />
        )}
      </CardContent>
    </Card>
  );
}

function SectionView({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <p className="text-sm text-muted-foreground">Not generated.</p>;
  if (typeof value === "string") return <p className="whitespace-pre-wrap text-sm">{value}</p>;
  if (typeof value === "number" || typeof value === "boolean") return <p className="text-sm">{String(value)}</p>;
  if (isStringArray(value)) return <ul className="list-disc space-y-1 pl-5 text-sm">{value.map((t, i) => <li key={i}>{t}</li>)}</ul>;
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((row, i) => (
          <div key={i} className="rounded-lg border p-3 text-sm"><ObjectRows obj={row as Record<string, unknown>} /></div>
        ))}
      </div>
    );
  }
  return <div className="text-sm"><ObjectRows obj={value as Record<string, unknown>} /></div>;
}

function humanKey(k: string) {
  return k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

function ObjectRows({ obj }: { obj: Record<string, unknown> }) {
  return (
    <div className="space-y-1.5">
      {Object.entries(obj ?? {}).map(([k, v]) => (
        <div key={k} className="grid gap-0.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-primary">{humanKey(k)}</span>
          {isStringArray(v) ? (
            <ul className="list-disc space-y-0.5 pl-5">{v.map((t, i) => <li key={i}>{t}</li>)}</ul>
          ) : typeof v === "object" && v !== null ? (
            <SectionView value={v} />
          ) : (
            <span className="whitespace-pre-wrap">{String(v)}</span>
          )}
        </div>
      ))}
    </div>
  );
}
